function getSelectedOptionsFromContainer(container) {
  // Single document-order query covers all three option representations —
  // <select> (dropdown), checked <input type="radio"> (buttons), and
  // <input type="hidden"> (single-value options with no visible picker,
  // SHOPSUP-3351) — so a product mixing a hidden single-value option with a
  // rendered multi-value one still returns values in the same position order
  // as product.variants[].options, which is what the '|'-joined variant map
  // lookup below requires.
  const elements = container.querySelectorAll(
    'select[name^="options["], input[type="radio"][name^="options["]:checked, input[type="hidden"][name^="options["]'
  );

  return Array.from(elements).map(element => element.value);
}

if (!customElements.get('product-info')) {
  class ProductInfo extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.productUrl = this.dataset.productUrl;
      this._abortController = null;
      this._unsubscribeVariant = null;
    }

    connectedCallback() {
      this._abortController = new AbortController();
      this.variantSelects = this.querySelector('variant-selects');

      this.notifyDataElement = this.querySelector('[data-notify]');
      this.notifyHandle = this.notifyDataElement?.dataset.notifyHandle || null;
      this.notifyDirect = this.notifyDataElement?.dataset.notifyDirect === 'true';
      this.notifySingleVariant = this.notifyDataElement?.dataset.notifySingleVariant === 'true';
      this.notifyButtonEligible = Boolean(this.notifyHandle) && (this.notifySingleVariant || !this.notifyDirect);
      this._notifyReady = false;

      this.submitButton = this.querySelector('.main-product__submit');
      this.submitButton?.addEventListener('click', this._onSubmitButtonClick.bind(this), {
        signal: this._abortController.signal,
      });

      if (this.notifyButtonEligible) {
        theme.utils.restockNotify.onReady(() => {
          this._notifyReady = true;
          if (this.submitButton?.dataset.notifyMode === 'true') {
            this.submitButton.disabled = false;
          }
        });
      }

      this._unsubscribeVariant = theme.pubsub?.subscribe(
        theme.pubsub.events.VARIANT_CHANGE,
        this._onVariantChange.bind(this)
      );
    }

    async _onSubmitButtonClick(event) {
      const button = event.currentTarget;
      if (button.dataset.notifyMode !== 'true') return;
      event.preventDefault();

      const variantId = button.dataset.notifyVariantId;
      if (!this.notifyHandle || !variantId || button.classList.contains('loading')) return;

      button.classList.add('loading');
      button.disabled = true;

      try {
        const payload = await theme.cache.request(`${window.Shopify.routes.root}products/${this.notifyHandle}.js`, {
          read: (response) => response.json(),
        });
        if (this._abortController.signal.aborted) return;

        if (button.dataset.notifyMode !== 'true' || button.dataset.notifyVariantId !== variantId) return;

        theme.utils.restockNotify.openModal(payload, Number(variantId));
      } catch (error) {
        if (error.name !== 'AbortError') console.error(error);
      } finally {
        button.classList.remove('loading');
        if (button.dataset.notifyMode === 'true') {
          button.disabled = !this._notifyReady;
        }
      }
    }

    disconnectedCallback() {
      this._abortController?.abort();
      this._unsubscribeVariant?.();
    }

    _onVariantChange(data) {
      if (data.sectionId !== this.sectionId) return;
      this._applyVariant(data.variant);
    }

    _applyVariant(variant) {
      this._updateURL(variant);
      this._updateVariantInput(variant);
      this._updatePriceDisplay(variant);
      this._updateSequraPrice(variant);
      this._updateAddToCartButton(variant);
      this._updateSku(variant);
      this._updateInventory(variant);
      this._updateUnitPrice(variant);
      this._updateMemberPrice(variant);
      this._updateGalleryMedia(variant);
    }

    _updateGalleryMedia(variant) {
      const mediaId = variant?.featured_media?.id;
      if (!mediaId) return;

      const slider = document.getElementById(`ProductGallery-${this.sectionId}`);
      if (typeof slider?.navigateToSlide !== 'function') return;

      const slide = slider.querySelector(`[data-media-id="${mediaId}"]`);
      if (!slide) return;

      const index = parseInt(slide.dataset.index, 10);
      if (Number.isNaN(index)) return;

      slider.navigateToSlide(index);
    }

    _updateURL(variant) {
      if (!variant || !this._isOnProductPage()) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    _isOnProductPage() {
      if (!this.productUrl) return true;
      try {
        return new URL(this.productUrl, window.location.origin).pathname === window.location.pathname;
      } catch (_) {
        return true;
      }
    }

    _updateVariantInput(variant) {
      if (!variant) return;
      const variantInput = this.querySelector('input[name="id"]');
      if (variantInput) variantInput.value = variant.id;
    }

    _updatePriceDisplay(variant) {
      if (!variant) return;

      const priceElement = this.querySelector('.price__amount');
      const compareElement = this.querySelector('.price__amount--compare');
      const priceWrapper = this.querySelector('.price');
      const saleBadge = this.querySelector('.price__badge-sale');
      const onSale = variant.available && variant.compare_at_price > variant.price;

      if (priceElement) {
        priceElement.textContent = theme.utils.formatMoney(variant.price);
      }

      if (priceWrapper) {
        priceWrapper.classList.toggle('price--on-sale', variant.compare_at_price > variant.price);
        priceWrapper.classList.toggle('price--sold-out', !variant.available);
      }

      if (compareElement) {
        if (variant.compare_at_price > variant.price) {
          compareElement.textContent = theme.utils.formatMoney(variant.compare_at_price);
          compareElement.parentElement.hidden = false;
        } else {
          compareElement.parentElement.hidden = true;
        }
      }

      if (saleBadge) {
        saleBadge.hidden = !onSale;

        if (onSale && saleBadge.dataset.showPercentage === 'true') {
          const percentage = Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
          saleBadge.textContent = (theme.locales?.product?.onSalePercentage || '').replace('[percentage]', percentage);
        }
      }
    }

    _updateSequraPrice(variant) {
      const sequraPrice = this.querySelector('[data-sequra-price]');
      if (!sequraPrice || !variant) return;

      sequraPrice.textContent = `${Math.floor(variant.price / 100)},${String(variant.price % 100).padStart(2, '0')}`;
    }

    _updateAddToCartButton(variant) {
      const button = this.submitButton || this.querySelector('.main-product__submit');
      const buttonText = this.querySelector('.main-product__submit-text');

      if (!button || !buttonText) return;

      if (!variant) {
        button.disabled = true;
        delete button.dataset.notifyMode;
        buttonText.textContent = theme.locales?.product?.unavailable;
        return;
      }

      if (variant.available) {
        button.disabled = false;
        delete button.dataset.notifyMode;
        buttonText.textContent = theme.locales?.product?.addToCart;
        return;
      }

      if (this.notifyButtonEligible) {
        button.dataset.notifyMode = 'true';
        button.dataset.notifyVariantId = variant.id;
        button.disabled = !this._notifyReady;
        buttonText.textContent = theme.locales?.product?.notify;
      } else {
        delete button.dataset.notifyMode;
        button.disabled = true;
        buttonText.textContent = theme.locales?.product?.soldOut;
      }
    }

    _updateSku(variant) {
      const skuBlock = this.querySelector('[data-sku]');
      if (!skuBlock) return;

      const skuValue = skuBlock.querySelector('.main-product__sku-value');
      const hasSku = variant?.sku && variant.sku.trim() !== '';

      skuBlock.hidden = !hasSku;
      if (skuValue) skuValue.textContent = hasSku ? variant.sku : '';
    }

    _updateInventory(variant) {
      const inventoryBlock = this.querySelector('[data-inventory]');
      if (!inventoryBlock || !variant) return;

      const mapScript = this.querySelector('[data-inventory-map]');
      if (!mapScript) return;

      let inventoryMap;
      try {
        inventoryMap = JSON.parse(mapScript.textContent);
      } catch (_) {
        return;
      }

      const data = inventoryMap[variant.id];
      if (!data) return;

      const threshold = parseInt(inventoryBlock.dataset.threshold, 10) || 10;
      const showCount = inventoryBlock.dataset.showCount === 'true';
      const { quantity, management, policy } = data;

      let status;
      if (!management) {
        status = 'in_stock';
      } else if (quantity > threshold) {
        status = 'in_stock';
      } else if (quantity > 0) {
        status = 'low_stock';
      } else if (policy === 'continue') {
        status = 'backorder';
      } else {
        status = 'out_of_stock';
      }

      const locales = theme.locales?.product?.inventory;
      let text;
      if (status === 'low_stock' && showCount) {
        text = (locales?.lowStockCount || '').replace('[count]', String(quantity));
      } else {
        const keyMap = { in_stock: 'inStock', low_stock: 'lowStock', backorder: 'backorder', out_of_stock: 'outOfStock' };
        text = locales?.[keyMap[status]] || '';
      }

      const statusSpan = inventoryBlock.querySelector('.main-product__inventory-status');
      if (statusSpan) {
        statusSpan.className = `main-product__inventory-status main-product__inventory-status--${status}`;
        statusSpan.textContent = text;
      }
    }

    _updateUnitPrice(variant) {
      const unitPriceBlock = this.querySelector('.price__unit-price');
      if (!unitPriceBlock || !variant) return;

      const mapScript = this.querySelector('[data-unit-price-map]');
      if (!mapScript) return;

      let unitPriceMap;
      try {
        unitPriceMap = JSON.parse(mapScript.textContent);
      } catch (_) {
        return;
      }

      const data = unitPriceMap[variant.id];
      const hasUnitPrice = data?.referenceUnit != null;

      unitPriceBlock.hidden = !hasUnitPrice;
      if (!hasUnitPrice) return;

      const valueElement = unitPriceBlock.querySelector('.price__unit-price-value');
      const referenceElement = unitPriceBlock.querySelector('.price__unit-price-reference');
      const unitElement = unitPriceBlock.querySelector('.price__unit-price-unit');

      if (valueElement) valueElement.textContent = theme.utils.formatMoney(data.unitPrice);
      if (referenceElement) {
        referenceElement.textContent = data.referenceValue;
        referenceElement.hidden = data.referenceValue === 1;
      }
      if (unitElement) unitElement.textContent = data.referenceUnit;
    }

    _updateMemberPrice(variant) {
      if (!variant) return;

      const memberPriceElement = this.querySelector('[data-member-price]');
      if (!memberPriceElement) return;

      const memberPriceSync = memberPriceElement.querySelector('member-price-sync');
      if (typeof memberPriceSync?.update === 'function') {
        memberPriceSync.update(variant);
        return;
      }

      memberPriceElement.hidden = variant.compare_at_price > variant.price;
      if (memberPriceElement.hidden) return;

      const amountElement = memberPriceElement.querySelector('.member-price__amount');
      if (!amountElement) return;

      const discountPercent = parseFloat(memberPriceElement.dataset.memberDiscount) || 0;
      const memberPrice = Math.round((variant.price * (100 - discountPercent)) / 100);

      amountElement.textContent = theme.utils.formatMoney(memberPrice);
    }
  }

  customElements.define('product-info', ProductInfo);
}

if (!customElements.get('variant-selects')) {
  class VariantSelects extends HTMLElement {
    constructor() {
      super();
      this._style = this.dataset.style || 'dropdown';
      this._abortController = null;
      this._notifyFetchController = null;
      this._variants = null;
      this._variantMap = null;
    }

    connectedCallback() {
      this._abortController = new AbortController();
      this.addEventListener('change', this._onSelectChange.bind(this), { signal: this._abortController.signal });
      this._buildCache();
      this._setUpNotify();
    }

    disconnectedCallback() {
      this._abortController?.abort();
      this._notifyFetchController?.abort();
    }

    _setUpNotify() {
      const notifyData = this.closest('product-info')?.querySelector('[data-notify]');
      this._notifyHandle = notifyData?.dataset.notifyHandle || null;
      this._notifyDirect = notifyData?.dataset.notifyDirect === 'true';
      this._notifyReady = false;

      if (this._notifyHandle && this._notifyDirect) {
        theme.utils.restockNotify.onReady(() => {
          this._notifyReady = true;
        });
      }
    }

    async _openNotifyModal(variant) {
      const variantId = variant.id;

      if (!this._notifyHandle) return;

      this._notifyFetchController?.abort();
      const controller = new AbortController();
      this._notifyFetchController = controller;

      try {
        const payload = await theme.cache.request(`${window.Shopify.routes.root}products/${this._notifyHandle}.js`, {
          read: (response) => response.json(),
        });
        if (controller.signal.aborted) return;

        const currentVariant = this._getCurrentVariant(this._getSelectedOptions());
        if (currentVariant?.id !== variantId) return;

        theme.utils.restockNotify.openModal(payload, variantId);
      } catch (error) {
        if (error.name !== 'AbortError') console.error(error);
      }
    }

    _buildCache() {
      const variantData = this.querySelector('[data-variants]');
      if (!variantData) return;

      this._variants = JSON.parse(variantData.textContent);
      this._variantMap = new Map();

      this._variants.forEach(variant => {
        this._variantMap.set(variant.options.join('|'), variant);
      });
    }

    _onSelectChange(event) {
      if (event.target === this) return;

      if (this._style === 'buttons' && event.target.type === 'radio') {
        this._updateButtonSelection(event.target);
      }

      this._updateVariantStatuses();

      const selectedOptions = this._getSelectedOptions();
      const variant = this._getCurrentVariant(selectedOptions);

      if (this._notifyDirect && this._notifyReady && variant && !variant.available) {
        this._openNotifyModal(variant);
      }

      theme.pubsub?.publish(theme.pubsub.events.VARIANT_CHANGE, {
        source: 'product-info',
        sectionId: this.dataset.sectionId,
        variant,
        selectedOptions,
      });
    }

    _updateButtonSelection(selectedInput) {
      const optionGroup = selectedInput.closest('.main-product__option');
      if (!optionGroup) return;

      optionGroup.querySelectorAll('.main-product__option-value').forEach(label => {
        label.classList.remove('main-product__option-value--selected');
      });

      selectedInput.closest('.main-product__option-value')?.classList.add('main-product__option-value--selected');
    }

    _updateVariantStatuses() {
      if (!this._variantMap || this._style !== 'buttons') return;
      this._updateButtonStates(this._getSelectedOptions());
    }

    _updateButtonStates(selectedOptions) {
      const optionGroups = this.querySelectorAll('.main-product__option');

      optionGroups.forEach((group, optionIndex) => {
        group.querySelectorAll('.main-product__option-value').forEach(button => {
          const input = button.querySelector('input[type="radio"]');
          if (!input) return;

          const testOptions = [...selectedOptions];
          testOptions[optionIndex] = input.value;

          const matchingVariant = this._variantMap.get(testOptions.join('|'));

          button.classList.toggle('main-product__option-value--disabled', !matchingVariant?.available);
          input.disabled = !matchingVariant;
        });
      });
    }

    _getCurrentVariant(selectedOptions) {
      if (!this._variantMap) return null;
      return this._variantMap.get(selectedOptions.join('|')) ?? null;
    }

    _getSelectedOptions() {
      return getSelectedOptionsFromContainer(this);
    }
  }

  customElements.define('variant-selects', VariantSelects);
}

if (!customElements.get('quantity-input')) {
  class QuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input[type="number"]');
      this.minusButton = this.querySelector('[name="minus"]');
      this.plusButton = this.querySelector('[name="plus"]');
      this.sectionId = this.dataset.section;
      this._abortController = null;
      this._unsubscribeVariant = null;
    }

    connectedCallback() {
      this._abortController = new AbortController();
      const signal = this._abortController.signal;
      this.minusButton?.addEventListener('click', this._decrease.bind(this), { signal });
      this.plusButton?.addEventListener('click', this._increase.bind(this), { signal });
      this.input?.addEventListener('change', this._validateInput.bind(this), { signal });

      this._unsubscribeVariant = theme.pubsub?.subscribe(
        theme.pubsub.events.VARIANT_CHANGE,
        this._onVariantChange.bind(this)
      );
    }

    disconnectedCallback() {
      this._abortController?.abort();
      this._unsubscribeVariant?.();
    }

    _onVariantChange(data) {
      if (data.sectionId !== this.sectionId) return;
      if (!data.variant?.quantity_rule) return;

      const { min, max, increment } = data.variant.quantity_rule;
      const currentValue = parseInt(this.input?.value, 10) || min;

      if (this.input) {
        this.input.min = min;
        this.input.step = increment;
        if (max !== null) {
          this.input.max = max;
        } else {
          this.input.removeAttribute('max');
        }

        const clampedMax = max !== null ? max : Infinity;
        const clamped = Math.max(min, Math.min(clampedMax, currentValue));
        if (clamped !== currentValue) {
          this.input.value = clamped;
        }
      }
    }

    _decrease() {
      if (!this.input) return;

      const currentValue = parseInt(this.input.value, 10) || 1;
      const min = parseInt(this.input.min, 10) || 1;
      const step = parseInt(this.input.step, 10) || 1;

      if (currentValue > min) {
        this.input.value = Math.max(min, currentValue - step);
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    _increase() {
      if (!this.input) return;

      const currentValue = parseInt(this.input.value, 10) || 1;
      const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
      const step = parseInt(this.input.step, 10) || 1;

      if (currentValue < max) {
        this.input.value = max === Infinity ? currentValue + step : Math.min(max, currentValue + step);
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    _validateInput() {
      if (!this.input) return;

      let value = parseInt(this.input.value, 10);
      const min = parseInt(this.input.min, 10) || 1;
      const max = parseInt(this.input.max, 10) || Infinity;

      if (isNaN(value) || value < min) {
        value = min;
      } else if (value > max) {
        value = max;
      }

      this.input.value = value;
    }
  }

  customElements.define('quantity-input', QuantityInput);
}

function initShareDropdown(container) {
  const dropdown = container.querySelector('theme-dropdown[data-mode="menu"]');
  if (!dropdown) return;

  const trigger = dropdown.querySelector('[data-dropdown-trigger]');
  const copyButton = dropdown.querySelector('[data-copy-url]');
  const toast = container.querySelector('.main-product__share-toast');
  const shareUrl = window.location.href;

  if (trigger && 'share' in navigator) {
    trigger.addEventListener('click', (event) => {
      event.stopImmediatePropagation();
      navigator.share({ url: shareUrl, title: document.title }).catch((error) => {
        if (error.name !== 'AbortError') {
          dropdown.open();
        }
      });
    }, true);
  }

  if (copyButton) {
    copyButton.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(shareUrl);
      } catch (_) {
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        textArea.style.cssText = 'position:fixed;left:-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      copyButton.classList.add('copied');
      if (toast) {
        toast.hidden = false;
        setTimeout(() => { toast.hidden = true; }, 2000);
      }
      setTimeout(() => copyButton.classList.remove('copied'), 2000);
    });
  }
}

document.querySelectorAll('.main-product__share').forEach(initShareDropdown);
